import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Send, Bot, User as UserIcon, Loader2 } from "lucide-react";

const Triage = () => {
  const { caseId } = useParams();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const url = caseId ? `/triage/questions?case_id=${caseId}` : "/triage/questions";
    api.get(url).then(({ data }) => setQuestions(data));
  }, [caseId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [answers, current]);

  const submitAnswer = async (overrideValue) => {
    const value = (overrideValue !== undefined ? overrideValue : draft).trim();
    if (!value) return;
    const q = questions[current];
    const newAnswers = [...answers, { question_id: q.id, question: q.q, answer: value }];
    setAnswers(newAnswers);
    setDraft("");

    if (current + 1 < questions.length) {
      setCurrent(current + 1);
    } else {
      setSubmitting(true);
      try {
        await api.post(`/cases/${caseId}/triage`, { answers: newAnswers });
        toast.success("Dossiê Inteligente RAF gerado");
        nav(`/caso/${caseId}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao processar triagem");
        setSubmitting(false);
      }
    }
  };

  const currentQ = questions[current];
  const hasOptions = Array.isArray(currentQ?.options) && currentQ.options.length > 0;

  const progress = questions.length ? Math.round((current / questions.length) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Triagem Inteligente RAF</div>
          <div className="text-xs text-slate-500 font-mono">{Math.min(current + 1, questions.length)}/{questions.length || "…"}</div>
        </div>
        <h1 className="font-display font-bold text-3xl text-[#0B2B62] tracking-tight">Vamos entender o caso juntos</h1>
        <div className="mt-4 h-1 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#0B2B62] transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div ref={scrollRef} className="bg-white border border-slate-200 rounded-xl p-6 h-[420px] overflow-y-auto space-y-5" data-testid="triage-chat">
        <div className="msg-in flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#0B2B62] text-white flex items-center justify-center shrink-0">
            <Bot className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 max-w-lg">
            <div className="text-xs text-slate-500 mb-1">Assistente RAF</div>
            <p className="text-slate-800 text-sm leading-relaxed">
              Olá. Vou fazer algumas perguntas rápidas para preparar o Dossiê Inteligente do caso.
              Responda com suas palavras — sem preocupação com formalidade.
            </p>
          </div>
        </div>

        {answers.map((a) => (
          <React.Fragment key={a.question_id}>
            <div className="msg-in flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-[#0B2B62] text-white flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 max-w-lg">
                <p className="text-slate-800 text-sm">{a.question}</p>
              </div>
            </div>
            <div className="msg-in flex items-start gap-3 flex-row-reverse">
              <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 text-slate-600 flex items-center justify-center shrink-0">
                <UserIcon className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="bg-[#0B2B62] text-white rounded-lg px-4 py-3 max-w-lg">
                <p className="text-sm">{a.answer}</p>
              </div>
            </div>
          </React.Fragment>
        ))}

        {!submitting && currentQ && (
          <div className="msg-in flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0B2B62] text-white flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 max-w-lg">
              <p className="text-slate-800 text-sm">{currentQ.q}</p>
              {hasOptions && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="triage-options">
                  {currentQ.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => submitAnswer(opt)}
                      data-testid={`triage-opt-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#0B2B62] text-[#0B2B62] font-semibold hover:bg-[#0B2B62] hover:text-white transition-colors"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {submitting && (
          <div className="msg-in flex items-center gap-3 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> A IA está gerando o Dossiê Inteligente RAF...
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <textarea
          data-testid="triage-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitAnswer(); } }}
          disabled={submitting || !currentQ}
          placeholder={hasOptions ? "Ou clique em uma opção acima…" : "Sua resposta..."}
          rows={2}
          className="flex-1 px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#0B2B62] outline-none resize-none disabled:bg-slate-50"
        />
        <button
          data-testid="triage-send"
          onClick={() => submitAnswer()}
          disabled={submitting || !draft.trim()}
          className="px-4 self-stretch bg-[#0B2B62] text-white rounded-md font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Triage;
